import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import CategoryTaskPicker from '../timesheetsComp/CategoryTaskPicker';
import { transformToHierarchy } from '../util/tree';
import SelectUsers from '../docsComponents/SelectUsers';
import '../docsComponents/addDocsModal.css';

interface AddModalProps {
  category: any,
  selectedCategoryId: any,
  handleClickCategoryBox: any,
  categoryName: any,
  isCategoryListVisible: any,
  handleCategoryListClick: any,
  handleNodeSelect: any,
  handleClickAddCategoryId: any,
  refreshCategoryDocs: any,
  active: any,
  setActive: any,
  handleCloseAll: any,
  none: any
}

type Mode = null | 'create' | 'upload';

const AddDocs: React.FC<AddModalProps> = ({
  category,
  selectedCategoryId,
  handleClickCategoryBox,
  categoryName,
  isCategoryListVisible,
  handleCategoryListClick,
  handleNodeSelect,
  handleClickAddCategoryId,
  refreshCategoryDocs,
  active,
  setActive,
  handleCloseAll,
  none
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dayLearn, setDeyLearn] = useState('10');
  const [selectedTasks, setSelectedTasks] = useState<any[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<Mode>(null);
  const [submitting, setSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const previewUrl = file ? URL.createObjectURL(file) : '';

  useEffect(() => {
    if (!active) setSelectedTasks([]);
  }, [active]);

  // revoke object URLs to avoid leaks
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const exec = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    sheetRef.current?.focus();
  };

  const handleFile = (f: File | null) => {
    if (!f) return;
    if (!/\.pdf$/i.test(f.name)) {
      enqueueSnackbar(t('OnlyPdf') as string, { variant: 'error' });
      return;
    }
    if (f.size > 1 * 1024 * 1024) {
      enqueueSnackbar('File size must be less than 1MB', { variant: 'error' });
      return;
    }
    setFile(f);
    setMode('upload');
  };

  const resetLeft = () => {
    setMode(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!title.trim()) {
      enqueueSnackbar('Enter name please', { variant: 'error' });
      return;
    }
    if (!selectedCategoryId.length || selectedCategoryId[0] === -1) {
      enqueueSnackbar('Please choose category', { variant: 'error' });
      return;
    }
    if (!mode) {
      enqueueSnackbar(t('CreateOrUpload') as string, { variant: 'error' });
      return;
    }

    const content = mode === 'create' ? (sheetRef.current?.innerHTML || '').trim() : '';
    if (mode === 'create' && !content) {
      enqueueSnackbar(t('EmptyDocument') as string, { variant: 'error' });
      return;
    }
    if (mode === 'upload' && !file) {
      enqueueSnackbar('Uploading file please', { variant: 'error' });
      return;
    }
    if (!active && (!selectedTasks || selectedTasks.length === 0)) {
      enqueueSnackbar('Please select users', { variant: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const metadata = {
        title,
        description,
        documentCategoryId: selectedCategoryId[0],
        isForAll: active,
        isExternal: mode === 'upload',
        day_learn: Number(dayLearn),
      } as any;

      if (mode === 'create') metadata.content = content;
      if (!active) metadata.userId = selectedTasks;

      const response = await axios.post('/document/add', metadata);
      const documentId = response.data?.id;
      if (!documentId) throw new Error('Document ID not returned from server');

      if (mode === 'upload' && file) {
        const formData = new FormData();
        formData.append('document', file);
        await axios.post(`document/upload/${documentId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else if (mode === 'create') {
        // editor docs have no upload step; the upload endpoint is what
        // normally creates the per-user "need to learn" records, so do the
        // equivalent here with a follow-up update of the audience.
        await axios.put(`document/${documentId}`, {
          isForAll: active,
          ...(active ? {} : { userId: selectedTasks }),
        });
      }

      enqueueSnackbar('Uploading success', { variant: 'success' });
      setFile(null);
      refreshCategoryDocs();
      handleCloseAll();
    } catch (error) {
      console.error('Error creating document:', error);
      enqueueSnackbar('Error uploading file', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // header badge + labels reflect the current mode
  const badge = mode === 'create' ? '📝' : mode === 'upload' ? 'PDF' : '＋';
  const leftTitle = mode === 'create' ? t('NewDocument')
    : mode === 'upload' ? (file?.name || t('Document'))
      : t('NoDocumentSelected');
  const leftSub = mode === 'create' ? t('EditorDraft')
    : mode === 'upload' && file ? `${(file.size / 1024).toFixed(0)} KB`
      : t('CreateOrUploadHint');

  return (
    <div className="adm-modal" role="presentation">
      <div className="adm-accent"><span style={{ width: mode ? '66%' : '33%' }} /></div>

      {/* header */}
      <div className="adm-head">
        <button className="adm-hbtn" title={t('Cancel') as string} onClick={handleCloseAll}>✕</button>
        <div className="adm-ttl">
          <h1>{t('AddDocument')}</h1>
          <div className="adm-sub">{t('CreateOrUploadHint')}</div>
        </div>
      </div>

      {/* body */}
      <div className="adm-body">

        {/* LEFT — choose / editor / preview */}
        <div className="adm-doc">
          <div className="adm-doc-bar">
            <div className="adm-badge" style={mode === 'create' ? { background: 'linear-gradient(135deg,#7c3aed,#a78bfa)' } : {}}>
              {badge}
            </div>
            <div>
              <div className="adm-t">{leftTitle}</div>
              <div className="adm-s">{leftSub}</div>
            </div>
            {mode && (
              <div className="adm-right">
                <button className="adm-mini-btn" onClick={resetLeft}>← {t('Change')}</button>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files ? e.target.files[0] : null)}
          />

          <div className="adm-stage">
            {/* 1) choice */}
            {!mode && (
              <div className="adm-choice">
                <div className="adm-hint">{t('HowAddDoc')}</div>
                <div className="adm-choice-cards">
                  <div className="adm-ccard create" onClick={() => { setMode('create'); setTimeout(() => sheetRef.current?.focus(), 50); }}>
                    <div className="adm-ci">📝</div>
                    <div className="adm-ct">{t('CreateDoc')}</div>
                    <div className="adm-cs">{t('OpenEditorHint')}</div>
                  </div>
                  <div className="adm-ccard upload" onClick={() => fileInputRef.current?.click()}>
                    <div className="adm-ci">📤</div>
                    <div className="adm-ct">{t('UploadFile')}</div>
                    <div className="adm-cs">{t('UploadFileHint')}</div>
                  </div>
                </div>
              </div>
            )}

            {/* 2) editor */}
            {mode === 'create' && (
              <div className="adm-editor">
                <div className="adm-tools">
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', 'h1')} title="H1">H1</button>
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', 'h2')} title="H2">H2</button>
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', 'p')} title={t('Text') as string}>¶</button>
                  <span className="adm-div" />
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} style={{ fontWeight: 900 }}>B</button>
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')} style={{ fontStyle: 'italic' }}>I</button>
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')} style={{ textDecoration: 'underline' }}>U</button>
                  <span className="adm-div" />
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')}>•</button>
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertOrderedList')}>1.</button>
                </div>
                <div className="adm-editor-scroll">
                  <div
                    ref={sheetRef}
                    className="adm-sheet"
                    contentEditable
                    suppressContentEditableWarning
                    data-ph={t('StartWriting') as string}
                  />
                </div>
              </div>
            )}

            {/* 3) preview */}
            {mode === 'upload' && file && (
              <div className="adm-preview">
                <iframe title={file.name} src={previewUrl} />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="adm-aside">
          <div className="adm-aside-scroll">
            <div className="adm-panel">
              <h4>{t('Information')}</h4>
              <div className="adm-field">
                <label>{t('Title')} <span className="adm-req">*</span></label>
                <input className="adm-inp" type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="adm-field">
                <label>{t('Description')}</label>
                <input className="adm-inp" type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <div className="adm-two">
                <div className="adm-field">
                  <label>{t('DayLearn')}</label>
                  <input
                    className="adm-inp"
                    type="text"
                    value={dayLearn}
                    onChange={(e) => { if (/^\d*$/.test(e.target.value)) setDeyLearn(e.target.value); }}
                  />
                </div>
                <div className="adm-field">
                  <label>{t('Category')} <span className="adm-req">*</span></label>
                  <CategoryTaskPicker
                    nodes={transformToHierarchy(category)}
                    selectedCategoryId={(selectedCategoryId?.[0] ?? -1) > 0 ? selectedCategoryId : []}
                    categoryName={categoryName}
                    onCategorySelect={(id) => { handleNodeSelect(id); handleClickAddCategoryId(Number(id)); }}
                    selectedTasks={[]}
                    setSelectedTasks={() => {}}
                    taskOrTasks={false}
                    categoryOnly
                    block
                    allowGroupSelect
                  />
                </div>
              </div>
            </div>

            <div className="adm-panel">
              <h4>{t('ChooseUsers')}</h4>
              <div className="adm-aud-row">
                <FormControlLabel
                  control={<Switch checked={active} onChange={() => setActive(!active)} />}
                  label={t('AllUsers')}
                />
              </div>
              {!active && (
                <SelectUsers selectedTasks={selectedTasks} setSelectedTasks={setSelectedTasks} />
              )}
            </div>
          </div>

          <div className="adm-foot">
            <Button
              variant="contained"
              sx={{ padding: '8px 10px', fontSize: 14, width: '100%', color: 'white !important' }}
              disabled={submitting}
              onClick={handleSubmit}>
              {t('Create')}
            </Button>
            <Button
              variant="outlined"
              sx={{ padding: '8px 10px', fontSize: 14, width: '100%' }}
              onClick={handleCloseAll}>
              {t('Cancel')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDocs;
